import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Alert, AlertTitle } from "@mui/material";
import api, { v4SessionSafe } from "../../../api/V4client";
import { DEVICES } from "../../../redux/constants/device.constants";
import { hasDeviceFlag, EMERIT_CONFIG_NOT_APPLIED } from "../utils";

// The device fetches its configuration when it likes, so only a read notices it.
const POLL_MS = 5000;

// Consecutive failed reads (~15s) before we stop claiming to know anything.
const MAX_ERRORS = 3;

/**
 * Warns that the configuration on the platform has not reached the device.
 * Mounted per device (`key={deviceId}`), so a device switch starts fresh and
 * closing the panel stops the poll.
 */
export default function ConfigSyncNotice({ deviceId }) {
  const { t } = useTranslation();
  // undefined until the first successful read: unknown, and we show nothing.
  const [configState, setConfigState] = useState(undefined);
  // A finished sync run can flip the flag; re-read rather than wait it out.
  const syncRunEvent = useSelector((s) => s[DEVICES].get("syncRunEvent"));
  const eventSeq =
    syncRunEvent?.deviceId === deviceId ? syncRunEvent?.seq || 0 : 0;

  const errors = useRef(0);
  // So a slow response can't land on top of a newer one.
  const req = useRef(0);

  useEffect(() => {
    if (!deviceId) return undefined;

    let cancelled = false;

    const read = () => {
      const token = (req.current += 1);
      return api
        .devicesVendorSpecificConfigStateList(deviceId, v4SessionSafe())
        .then((res) => {
          if (cancelled || token !== req.current) return;
          errors.current = 0;
          setConfigState(res?.data || {});
        })
        .catch(() => {
          if (cancelled || token !== req.current) return;
          errors.current += 1;
          if (errors.current >= MAX_ERRORS) setConfigState(undefined);
        });
    };

    read();
    const id = setInterval(read, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, eventSeq]);

  if (!hasDeviceFlag(configState, EMERIT_CONFIG_NOT_APPLIED)) return null;

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>
        {t("configNotApplied", { defaultValue: "Configuration not applied" })}
      </AlertTitle>
      {t("configNotAppliedOnDevice", {
        defaultValue:
          "The device has not downloaded this configuration yet. Launch the configuration procedure on the device to load and apply it.",
      })}
    </Alert>
  );
}
